import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { createCredential, updateCredential, deleteCredential } from '../CredentialsServices';
import { resolveBackendMessage } from '../../../Utils/apiMessages';

/**
 * Custom hook to handle credential CRUD actions
 * @param {Function} refetchCredentials - Function to refetch credentials after action
 * @param {Function} handleError - Error handler function
 * @returns {Object} Action handlers and loading states
 */
const useCredentialActions = (refetchCredentials, handleError) => {
    const { t } = useTranslation();
    const [createLoading, setCreateLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState({});

    const handleCreate = useCallback(async (values, form = null) => {
        setCreateLoading(true);
        try {
            const payload = await createCredential(values);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            refetchCredentials();
            return true;
        } catch (error) {
            handleError(error, form);
            return false;
        } finally {
            setCreateLoading(false);
        }
    }, [refetchCredentials, handleError, t]);

    const handleUpdate = useCallback(async (id, values, record) => {
        setUpdateLoading(true);
        try {
            // 1. Update Credential
            // Filter values to only include allowed fields per API (name, short_lived_token)
            const payload = {};
            if (values.name !== undefined) payload.name = values.name;
            if (values.short_lived_token !== undefined) payload.short_lived_token = values.short_lived_token;

            const credentialPayload = await updateCredential(id, payload);
            let text = resolveBackendMessage(t, credentialPayload);


            if (text) message.success(text);
            refetchCredentials();
            return true;
        } catch (error) {
            handleError(error);
            return false;
        } finally {
            setUpdateLoading(false);
        }
    }, [refetchCredentials, handleError, t]);

    const handleDelete = useCallback(async (id) => {
        setDeleteLoading(prev => ({ ...prev, [id]: true }));
        try {
            const payload = await deleteCredential(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            refetchCredentials();
            return true;
        } catch (error) {
            handleError(error);
            return false;
        } finally {
            setDeleteLoading(prev => ({ ...prev, [id]: false }));
        }
    }, [refetchCredentials, handleError, t]);

    return {
        handleCreate,
        handleUpdate,
        handleDelete,
        createLoading,
        updateLoading,
        deleteLoading,
    };
};

export default useCredentialActions;
